import { selectLogoImage } from '../../store/workspace/selectors';
import { useDispatch, useSelector } from '../../store/store';
import { setNotification } from '../../store/general/slice';
import { PostedJob } from '../../store/workspace/types';
import { useNavigate } from 'react-router-dom';
import styles from './style.module.scss';
import { useState } from 'react';
import Button from '../Button';
import {
    editJobThunk,
    getJobsThunk,
} from '../../store/workspace/thunks';
import {
    setSelectedJob,
    setJobDetailModal,
    setCopyJobLinkWarningModal,
    setEditJobExamDeadlineModal,
} from '../../store/workspace/slice';
import {
    EditIcon,
    CopyIcon,
    AshbyLogoIcon,
    MenuboardCloseIcon,
    ClipboardCloseIcon,
} from '../../assets/icons';

interface Props {
    detail: PostedJob;
};

const applicantURL = process.env.REACT_APP_APPLICANT_BASE_URL || '';

function JobCard(props: Props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const logo = useSelector(selectLogoImage);
    const [copied, setCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const isCopyJobLinkWarningDismissed = localStorage.getItem('copyJobLinkWarningDismissed') === 'true';

    const {
        detail: {
            id,
            city,
            title,
            status,
            jobType,
            platform,
            companyName,
            lastModified,
            workplaceType,
            applicantCounter,
            applicantCodingCounter,
            applicantMultipleCounter,
            applicantRecruitingCounter,
            multipleChoiceExamDeadline,
        } } = props;

    const postedDate = lastModified.slice(0, 10).split('-');

    const copyToClipboard = () => {
        navigator?.clipboard?.writeText(`${applicantURL}/job?jobId=${id}&jobTitle=${title}`).then(() => {
            setCopied(true);
            setTimeout(() => {
                setCopied(false)
            }, 3000);
        })

        if (!isCopyJobLinkWarningDismissed && platform === 'Ashby') {
            dispatch(setCopyJobLinkWarningModal(true));
        }
    };

    const primaryAction = () => {
        if (status === 'unposted') {
            setIsLoading(true);
            dispatch(editJobThunk({
                jobId: id,
                action: 'publish',
            }))
                .unwrap()
                .then(() => {
                    dispatch(getJobsThunk({}));
                })
                .catch(e => {
                    dispatch(setNotification(['Somthing went wrong...', 'error']))
                })
                .finally(() => {
                    setIsLoading(false);
                })
        } else {
            navigate(`/workspace/history/${id}`)
        }
    };

    const openJobDetail = () => {
        dispatch(setSelectedJob(props.detail));
        dispatch(setJobDetailModal(true));
    };

    const getDeadline = () => {
        const [month, day, year] = multipleChoiceExamDeadline.split('-');
        const deadline = new Date(Number(year), Number(month) - 1, Number(day));
        const today = new Date();
        const diffTime = deadline.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays <= 10 && diffDays > 0
            ? <b data-text-color='red'>({`${diffDays} day${diffDays > 1 ? 's' : ''} left`})</b>
            : null
    }

    const openEditExamDeadlineModal = () => {
        dispatch(setSelectedJob(props.detail));
        dispatch(setEditJobExamDeadlineModal(true));
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.head}>
                <div className={styles.head_group}>
                    <div className={styles.head_group_logo}>
                        {logo ? <img src={logo} alt='' /> :
                            <span>{companyName?.slice(0, 1)}</span>}
                    </div>
                    <div className={styles.head_group_text}>
                        <span className={styles.title}>{title}</span>
                        <div className={styles.city}>
                            <span>{city?.city}</span>
                            {platform === 'Ashby' && <AshbyLogoIcon width={53} height={17} />}
                        </div>
                    </div>
                </div>
                <div>
                    {(status === 'posted') &&
                        <div className={styles.copyToClipboard}>
                            <CopyIcon />
                            <span
                                onClick={copyToClipboard}
                                className={styles.copyLink}
                            >
                                {copied ? 'Link copied!' : 'Exam share link'}
                            </span>
                            <div className={styles.copyToClipboard_tooltip}>
                                Click <b>“Exam Share Link”</b> on the job card
                                to share the exam with potential applicants
                            </div>
                        </div>}

                    {status === 'ended' && <div className={styles.expired}>
                        <MenuboardCloseIcon />
                        <span>Ended</span>
                    </div>}

                    {status === 'expired' && <div className={styles.expired}>
                        <ClipboardCloseIcon />
                        <span>Expired</span>
                    </div>}
                </div>
            </div>
            <div className={styles.body}>
                <div className={styles.body_row}>
                    <span className={styles.body_row_text}>
                        {jobType?.title}-{workplaceType?.title}
                    </span>
                </div>
                <div className={styles.body_row}>
                    <div className={styles.body_row_text}>
                        Posted at: <b>{`${postedDate[1]}-${postedDate[2]}-${postedDate[0]}`}</b>
                    </div>
                </div>
                <div className={styles.body_row}>
                    <div className={styles.body_row_text}>
                        Exam deadline: <b>{multipleChoiceExamDeadline}</b> {getDeadline()}
                    </div>
                    {(status === 'posted') && <EditIcon onClick={openEditExamDeadlineModal} />}
                </div>
            </div>
            <div className={styles.actions}>
                <Button
                    type='Outline-Dark'
                    text={status === 'unposted' ? 'Review & Edit' : 'Job Information'}
                    onClick={openJobDetail}
                    className={styles.actions_btn}
                />
                <Button
                    type='Primary'
                    onClick={primaryAction}
                    loading={isLoading}
                    className={styles.actions_btn}
                    text={status === 'unposted' ? 'Publish Job' : 'Exams Statistic'}
                />
                <div className={styles.actions_applicantCounter}>
                    <div className={styles.actions_applicantCounter_inner}>
                        <span className={styles.actions_applicantCounter_default}>
                            Applicants: {applicantCounter}
                        </span>
                        <div className={styles.actions_applicantCounter_details}>
                            <div className={styles.actions_applicantCounter_item}>
                                <span className={styles.actions_applicantCounter_dot} style={{ backgroundColor: '#FF9800' }}></span>
                                <span>Recruiting: {applicantRecruitingCounter}</span>
                            </div>
                            <div className={styles.actions_applicantCounter_item}>
                                <span className={styles.actions_applicantCounter_dot} style={{ backgroundColor: '#2196F3' }}></span>
                                <span>Multiple Choice: {applicantMultipleCounter}</span>
                            </div>
                            <div className={styles.actions_applicantCounter_item}>
                                <span className={styles.actions_applicantCounter_dot} style={{ backgroundColor: '#4CAF50' }}></span>
                                <span>Coding: {applicantCodingCounter}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default JobCard;
